import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { WizardLayout } from '../components/WizardLayout';
import { api, type OrderStatus } from '../lib/api';

/**
 * Polls the order endpoint until status moves past PENDING_PAYMENT, then
 * shows the appropriate state. The Stripe webhook is what flips the order
 * to PAID server-side; we just observe it here.
 */
export function Success() {
  const { orderId } = useParams();
  const [order, setOrder] = useState<OrderStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    let alive = true;
    let timer: number;
    const tick = async () => {
      try {
        const o = await api.getOrder(orderId);
        if (!alive) return;
        setOrder(o);
        // Keep polling while we wait for the webhook (PAID) or filing job
        // (ACCEPTED / REJECTED).
        if (o.status !== 'ACCEPTED' && o.status !== 'REJECTED') {
          timer = window.setTimeout(tick, 2000);
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : 'Lookup failed');
      }
    };
    tick();
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [orderId]);

  if (error)
    return (
      <WizardLayout step="success">
        <div className="card text-red-700">{error}</div>
      </WizardLayout>
    );
  if (!order)
    return (
      <WizardLayout step="success">
        <div className="card">Loading…</div>
      </WizardLayout>
    );

  return (
    <WizardLayout step="success">
      <div className="card text-center">
        <Status order={order} />
      </div>
    </WizardLayout>
  );
}

function Status({ order }: { order: OrderStatus }) {
  switch (order.status) {
    case 'PENDING_PAYMENT':
      return (
        <>
          <Spinner />
          <h1 className="mt-4 text-2xl font-semibold text-slate-900">Confirming payment…</h1>
          <p className="mt-2 text-sm text-slate-600">
            Stripe is letting us know about your payment. This usually takes a couple of seconds.
          </p>
        </>
      );
    // PAID / SUBMITTING / REJECTED all show the same friendly "in progress"
    // message. We deliberately do NOT surface a REJECTED verdict to the
    // customer here — an early rejection is often transient (e.g. CH poll
    // error 8023 "EF documents not found" which just means the filing is
    // still being processed on their side). Admin sees the real status in
    // the admin panel and follows up manually if a genuine rejection needs
    // customer action. Showing "couldn't accept" to the customer here has
    // caused people to duplicate-file manually, so we never do it.
    case 'PAID':
    case 'SUBMITTING':
    case 'REJECTED':
      return (
        <>
          <CheckCircle />
          <h1 className="mt-4 text-2xl font-semibold text-slate-900">
            Thank you for filing with Dormant Accounts Filing
          </h1>
          <p className="mt-3 text-sm text-slate-600">
            Your accounts filing is in process with Companies House. Once the
            filing is updated on the register, we will acknowledge you over a
            separate email within a day.
          </p>
          <p className="mt-3 text-sm text-slate-600">
            <strong>Please do not file separately in the meantime</strong> — it
            would create a duplicate submission with Companies House.
          </p>
          <p className="mt-3 text-sm text-slate-600">
            If you have any queries, please send us an email at{' '}
            <a
              href="mailto:contact.dormantfiling@gmail.com"
              className="text-brand-700 underline-offset-2 hover:underline"
            >
              contact.dormantfiling@gmail.com
            </a>
            .
          </p>
          <p className="mt-6 text-sm">
            <a
              href={`/api/orders/${order.id}/ixbrl`}
              target="_blank"
              rel="noreferrer"
              className="text-brand-600 underline-offset-2 hover:underline"
            >
              Preview your filing →
            </a>
          </p>
        </>
      );
    case 'ACCEPTED':
      return (
        <>
          <CheckCircle />
          <h1 className="mt-4 text-2xl font-semibold text-slate-900">Filing accepted</h1>
          <p className="mt-2 text-sm text-slate-600">
            Companies House has accepted your dormant accounts.
          </p>
          {order.chAckNumber && (
            <p className="mt-3 text-xs text-slate-500">
              Acknowledgement number: <code>{order.chAckNumber}</code>
            </p>
          )}
          <p className="mt-3 text-xs text-slate-500">
            A receipt has been emailed to you.
          </p>
        </>
      );
  }
}

function Spinner() {
  return (
    <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
  );
}
function CheckCircle() {
  return (
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-2xl text-green-700">
      ✓
    </div>
  );
}
